/**
 * Supplier connection detail (C5) — pushed OVER the tabs (title "Connection", with back).
 *
 * Shows the connection's status, capabilities and metadata, plus actions:
 *   "Check health" → POST /api/supplier-connections/:id/health
 *   "Sync prices"  → POST /api/supplier-connections/:id/sync
 *   "Unlink"       → POST /api/supplier-connections/:id/unlink
 *
 * Connection rows are raw snake_case; status mapped to a Badge locally (statusMap.ts is
 * shared, not modified). NEVER imports @smartbox/*. No raw style values.
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getConnection,
  healthConnection,
  syncConnection,
  unlinkConnection,
  type ConnectionStatus,
} from '../../../src/api/pricing';
import { Screen, Text, Card, Button, Badge } from '../../../src/components/ui';
import { theme, type StatusKey } from '../../../src/theme';
import { extractErrorMessage, humanize, formatDateTime } from '../../../src/lib/format';

function connectionBadge(status: ConnectionStatus): { key: StatusKey; label: string } {
  switch (status) {
    case 'linked':
      return { key: 'paid', label: 'Linked' };
    case 'pending':
      return { key: 'inProgress', label: 'Pending' };
    case 'error':
      return { key: 'overdue', label: 'Error' };
    case 'expired':
      return { key: 'overdue', label: 'Expired' };
    default:
      return { key: 'draft', label: 'Unlinked' };
  }
}

export default function ConnectionDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNote, setActionNote] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['supplier-connections', id],
    queryFn: () => getConnection(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const healthMutation = useMutation({
    mutationFn: () => healthConnection(id!),
    onMutate: () => {
      setActionError(null);
      setActionNote(null);
    },
    onSuccess: () => {
      setActionNote('Health check complete.');
      void queryClient.invalidateQueries({ queryKey: ['supplier-connections', id] });
    },
    onError: (err) => setActionError(extractErrorMessage(err, 'Health check failed.')),
  });

  const syncMutation = useMutation({
    mutationFn: () => syncConnection(id!),
    onMutate: () => {
      setActionError(null);
      setActionNote(null);
    },
    onSuccess: () => {
      setActionNote('Price sync started.');
      void queryClient.invalidateQueries({ queryKey: ['supplier-connections', id] });
      void queryClient.invalidateQueries({ queryKey: ['price-imports'] });
    },
    onError: (err) => setActionError(extractErrorMessage(err, 'Sync failed.')),
  });

  const unlinkMutation = useMutation({
    mutationFn: () => unlinkConnection(id!),
    onMutate: () => {
      setActionError(null);
      setActionNote(null);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['supplier-connections', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['supplier-connections'] });
      setActionNote('Connection unlinked.');
    },
    onError: (err) => setActionError(extractErrorMessage(err, 'Unlink failed.')),
  });

  if (!id) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="body" color="danger">
            No connection ID provided.
          </Text>
        </View>
      </Screen>
    );
  }

  if (isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading connection"
          />
        </View>
      </Screen>
    );
  }

  if (isError || !data) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(error, 'Failed to load connection.')}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => refetch()}
            />
          </Card>
        </View>
      </Screen>
    );
  }

  const b = connectionBadge(data.status);
  const title = data.display_name ?? data.connector_code ?? data.id.slice(0, 8).toUpperCase();
  const capabilities = data.capabilities ?? [];
  const busy = healthMutation.isPending || syncMutation.isPending || unlinkMutation.isPending;
  const isLinked = data.status === 'linked';

  return (
    <Screen scroll>
      <View style={styles.statusRow}>
        <Text variant="title">{title}</Text>
        <Badge status={b.key} label={b.label} />
      </View>

      <Card>
        <Row label="Status" value={humanize(data.status)} />
        {data.connector_code ? <Row label="Connector" value={data.connector_code} /> : null}
        {data.account_number ? <Row label="Account" value={data.account_number} /> : null}
        {data.link_method ? <Row label="Link method" value={humanize(data.link_method)} /> : null}
        {data.last_synced_at ? (
          <Row label="Last synced" value={formatDateTime(data.last_synced_at)} />
        ) : null}
      </Card>

      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Capabilities
        </Text>
        {capabilities.length === 0 ? (
          <Text variant="body" color="secondary">
            No capabilities reported yet.
          </Text>
        ) : (
          <View style={styles.chips}>
            {capabilities.map((cap) => (
              <Badge key={cap} status="sent" label={humanize(cap)} />
            ))}
          </View>
        )}
      </View>

      {actionError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {actionError}
          </Text>
        </Card>
      ) : actionNote ? (
        <Card>
          <Text variant="body" color="secondary">
            {actionNote}
          </Text>
        </Card>
      ) : null}

      <View style={styles.actions}>
        <Button
          title="Check health"
          variant="secondary"
          loading={healthMutation.isPending}
          disabled={busy}
          onPress={() => healthMutation.mutate()}
          accessibilityLabel="Check connection health"
        />
        <Button
          title="Sync prices"
          loading={syncMutation.isPending}
          disabled={busy}
          onPress={() => syncMutation.mutate()}
          accessibilityLabel="Sync prices"
        />
        <Button
          title="Unlink"
          variant="danger"
          loading={unlinkMutation.isPending}
          disabled={busy || !isLinked}
          onPress={() => unlinkMutation.mutate()}
          accessibilityLabel="Unlink connection"
        />
      </View>
    </Screen>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.cardRow}>
      <Text variant="small" color="secondary">
        {label}
      </Text>
      <Text variant="data">{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  section: { gap: theme.spacing.sm },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actions: { gap: theme.spacing.sm },
});
